import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SOURCE_NAMES, type SourceName, type StreamRef } from '@/types/streaming';
import { preflightSource } from '@/services/streamingApi';

type RowStatus = 'idle' | 'probing' | 'ok' | 'fail';

interface RowState {
  status: RowStatus;
  kind?: string;
  message?: string;
}

interface Props {
  streamRef: StreamRef;
  activeSource: SourceName | null;
  onSelect: (next: SourceName | null) => void;
}

function refKey(ref: StreamRef): string {
  if (ref.type === 'movie') return `movie:${ref.tmdbId}`;
  if (ref.type === 'anime') return `anime:${ref.malId}:${ref.episode}:${ref.audio}`;
  return `tv:${ref.tmdbId}:${ref.season}:${ref.episode}`;
}

const INITIAL: Record<SourceName, RowState> = {
  vidlink: { status: 'idle' },
  vidzee: { status: 'idle' },
  vidrock: { status: 'idle' },
  moviesapi: { status: 'idle' },
  'moviesapi-vip': { status: 'idle' },
  videasy: { status: 'idle' },
  flickygate: { status: 'idle' },
  purstream: { status: 'idle' },
  klikxxi: { status: 'idle' },
  hollymoviehd: { status: 'idle' },
  'animekai-sub': { status: 'idle' },
  'animekai-dub': { status: 'idle' },
  'miruro-sub': { status: 'idle' },
  'miruro-dub': { status: 'idle' },
  'animepahe-sub': { status: 'idle' },
  'animepahe-dub': { status: 'idle' },
  vaplayer: { status: 'idle' },
  aether: { status: 'idle' },
  ridomovies: { status: 'idle' },
  vixsrc: { status: 'idle' },
  showbox: { status: 'idle' },
};

export function SourceMenu({ streamRef, activeSource, onSelect }: Props): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Record<SourceName, RowState>>(INITIAL);
  const containerRef = useRef<HTMLDivElement>(null);
  const inProgressRef = useRef<SourceName | null>(null);
  const mountedRef = useRef(true);
  const key = refKey(streamRef);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setRows(INITIAL);
    setOpen(false);
  }, [key]);

  useEffect(() => {
    if (!open) return;
    function onClickAway(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [open]);

  const triggerLabel = t('source.trigger', { name: activeSource ?? t('source.auto') });

  async function probeAndSelect(name: SourceName) {
    inProgressRef.current = name;
    setRows((r) => ({ ...r, [name]: { status: 'probing' } }));
    try {
      const result = await preflightSource(streamRef, name);
      if (!mountedRef.current || inProgressRef.current !== name) return;
      if (result.ok) {
        setRows((r) => ({ ...r, [name]: { status: 'ok' } }));
        onSelect(name);
      } else {
        setRows((r) => ({ ...r, [name]: { status: 'fail', kind: result.kind, message: result.message } }));
      }
    } catch (err) {
      if (!mountedRef.current || inProgressRef.current !== name) return;
      setRows((r) => ({
        ...r,
        [name]: { status: 'fail', kind: 'unknown', message: (err as Error).message ?? 'request failed' },
      }));
    }
  }

  function statusGlyph(s: RowStatus): string {
    if (s === 'probing') return '...';
    if (s === 'ok') return 'OK';
    if (s === 'fail') return 'X';
    return '';
  }

  return (
    <div ref={containerRef} className="source-menu" data-testid="source-menu">
      <button
        type="button"
        className="source-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {triggerLabel}
      </button>
      {open && (
        <div role="menu" className="source-menu__menu">
          <button
            type="button"
            role="menuitem"
            data-status="idle"
            onClick={() => { onSelect(null); setOpen(false); }}
            className={`source-menu__item${activeSource === null ? ' is-active' : ''}`}
          >
            <span>{t('source.auto')}</span>
          </button>
          {SOURCE_NAMES.map((name) => {
            const state = rows[name];
            return (
              <button
                key={name}
                type="button"
                role="menuitem"
                data-status={state.status}
                title={state.status === 'fail' ? `${state.kind ?? ''}: ${state.message ?? ''}` : undefined}
                onClick={() => probeAndSelect(name)}
                className={`source-menu__item${activeSource === name ? ' is-active' : ''}`}
              >
                <span>{name}</span>
                <span aria-hidden="true">{statusGlyph(state.status)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
